// ---------------------------------------------------------------------------
// Story fidelity (CSF) — which narration context an Echo was written with.
//
// Source of truth: the orchestrator's script envelope, `diagnostics.story_fidelity`
// (`story-fidelity-summary.v1`, CSF implementation contract §8). It is written
// whenever a story-fidelity receipt exists; absent means the Echo predates the
// system or is not B2C. Fallback: `script.narration_plan.story_context.customer_story`
// (contract §4) only exists on the treatment arm, so its presence alone proves
// treatment even when the summary is missing.
//
// Badge rules (contract §9), first match wins: context_in_narration == true →
// new context (treatment); arm == "treatment" with a `fallback` reason → old
// context, the treatment fell back after an authoring failure (CSF-H1, §8);
// summary present otherwise → old context, A/B control; summary absent → old
// context, before story fidelity.
//
// Pure and total: every input, including malformed JSON, yields a value.
// ---------------------------------------------------------------------------

export const STORY_FIDELITY_SUMMARY_SCHEMA = "story-fidelity-summary.v1";
const SUMMARY_SCHEMA_PATTERN = /^story-fidelity-summary\.v\d+$/;

export const STORY_COVERED_VALUES = [
  "spoken",
  "shown",
  "title_card",
  "dropped",
  "missing",
] as const;
export type StoryCovered = (typeof STORY_COVERED_VALUES)[number];

export type StoryLedgerItem = {
  id: string;
  glossEn: string;
  quote: string;
  visual: boolean;
  mustSay: boolean;
  /** Strategy treatment: say | show | say_and_show | title_card | drop | say_optional. */
  treatment: string | null;
  /** Delivered coverage; null when it was never measured (e.g. fallback source). */
  covered: StoryCovered | null;
};

export type StoryCoverage = {
  stage: string | null;
  spoken: number | null;
  mustSay: number | null;
  /** pass | shortfall | suppressed | unavailable */
  status: string | null;
};

export type StoryFidelityDetail = {
  /** Where the verdict came from: the CRM summary, or the treatment-only view. */
  source: "summary" | "narration_plan";
  arm: "treatment" | "control" | null;
  experimentId: string | null;
  ruleId: string | null;
  /** The rule id names the internal-team treatment rule. */
  isInternal: boolean;
  /** ok | unavailable | empty | unverified | too_small | disabled */
  ledgerStatus: string | null;
  itemCount: number | null;
  mustSayCount: number | null;
  policyDigest: string | null;
  releaseId: string | null;
  coverage: StoryCoverage | null;
  ledger: StoryLedgerItem[];
  /**
   * Why a treatment Echo was re-authored with the old (control) context, e.g.
   * "legacy_after_authoring_failure" (CSF-H1). Null when the summary has none.
   */
  fallback: string | null;
};

export type StoryFidelity =
  | { kind: "loading" }
  | { kind: "unavailable"; reason: "no_script" | "script_error" }
  | { kind: "legacy" }
  | ({ kind: "treatment" } & StoryFidelityDetail)
  /** Treatment arm, but the delivered narration used the old context. */
  | ({ kind: "treatment_fallback" } & StoryFidelityDetail & {
        fallback: string;
      })
  | ({ kind: "control" } & StoryFidelityDetail);

export type StoryFidelityKind = StoryFidelity["kind"];

/** How far the page got with the script JSON. */
export type ScriptLoadStatus = "loading" | "loaded" | "error" | "none";

export function readStoryFidelity(
  scriptEnvelope: unknown,
  status: ScriptLoadStatus = "loaded",
): StoryFidelity {
  if (status === "loading") return { kind: "loading" };
  if (status === "error")
    return { kind: "unavailable", reason: "script_error" };
  if (status === "none") return { kind: "unavailable", reason: "no_script" };
  try {
    return parseEnvelope(scriptEnvelope);
  } catch {
    // Unreachable by construction; the badge must never take the page down.
    return { kind: "legacy" };
  }
}

/** Map the page's script loader state onto the parser's load status. */
export function scriptLoadStatus(input: {
  status: "idle" | "loading" | "loaded" | "error";
  /** The signed URL or the media list is still resolving. */
  loading?: boolean;
  /** The signed URL could not be created. */
  urlError?: boolean;
}): ScriptLoadStatus {
  if (input.status === "loaded") return "loaded";
  if (input.status === "loading" || input.loading) return "loading";
  if (input.status === "error" || input.urlError) return "error";
  return "none";
}

function parseEnvelope(scriptEnvelope: unknown): StoryFidelity {
  const envelope = record(scriptEnvelope);
  const summary = record(record(envelope?.diagnostics)?.story_fidelity);
  const view = customerStoryView(envelope);

  if (summary && isSummarySchema(summary.schema_version)) {
    const ruleId = text(summary.rule_id);
    const ledger = ledgerItems(summary.ledger);
    const detail: StoryFidelityDetail = {
      source: "summary",
      arm: armOf(summary.arm),
      experimentId: text(summary.experiment_id),
      ruleId,
      isInternal: isInternalRule(ruleId),
      ledgerStatus: text(summary.ledger_status),
      itemCount: count(summary.items),
      mustSayCount: count(summary.must_say),
      policyDigest: text(summary.policy_digest),
      releaseId: text(summary.release_id),
      coverage: coverageOf(summary.coverage),
      ledger: ledger.length > 0 ? ledger : viewItems(view),
      fallback: text(summary.fallback),
    };
    if (summary.context_in_narration === true) {
      return { kind: "treatment", ...detail };
    }
    if (detail.arm === "treatment" && detail.fallback !== null) {
      return {
        kind: "treatment_fallback",
        ...detail,
        fallback: detail.fallback,
      };
    }
    return { kind: "control", ...detail };
  }

  if (view) {
    const items = viewItems(view);
    return {
      kind: "treatment",
      source: "narration_plan",
      arm: "treatment",
      experimentId: null,
      ruleId: null,
      isInternal: false,
      ledgerStatus: null,
      itemCount: items.length,
      mustSayCount: items.filter((item) => item.mustSay).length,
      policyDigest: null,
      releaseId: null,
      coverage: null,
      ledger: items,
      fallback: null,
    };
  }

  return { kind: "legacy" };
}

/** `narration_plan.story_context.customer_story`, present on treatment only. */
function customerStoryView(
  envelope: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const plan =
    record(record(envelope?.script)?.narration_plan) ??
    record(envelope?.narration_plan);
  return record(record(plan?.story_context)?.customer_story);
}

function viewItems(view: Record<string, unknown> | null): StoryLedgerItem[] {
  if (!view) return [];
  const mustSay = ledgerItems(view.must_say, true);
  const seen = new Set(mustSay.map((item) => item.id));
  const others = ledgerItems(view.other_items, false).filter(
    (item) => !seen.has(item.id),
  );
  return [...mustSay, ...others];
}

function ledgerItems(value: unknown, mustSay?: boolean): StoryLedgerItem[] {
  if (!Array.isArray(value)) return [];
  const items: StoryLedgerItem[] = [];
  value.forEach((entry, index) => {
    const item = record(entry);
    if (!item) return;
    const glossEn = text(item.gloss_en) ?? "";
    const quote = text(item.quote) ?? "";
    if (!glossEn && !quote) return;
    items.push({
      id: text(item.id) ?? `#${index + 1}`,
      glossEn,
      quote,
      visual: item.visual === true,
      mustSay: mustSay ?? item.must_say === true,
      treatment: text(item.treatment),
      covered: coveredOf(item.covered),
    });
  });
  return items;
}

function coverageOf(value: unknown): StoryCoverage | null {
  const coverage = record(value);
  if (!coverage) return null;
  return {
    stage: text(coverage.stage),
    spoken: count(coverage.spoken),
    mustSay: count(coverage.must_say),
    status: text(coverage.status),
  };
}

function isSummarySchema(value: unknown): boolean {
  return typeof value === "string" && SUMMARY_SCHEMA_PATTERN.test(value);
}

export function isInternalRule(ruleId: string | null): boolean {
  return ruleId !== null && ruleId.includes("internal-team");
}

function armOf(value: unknown): StoryFidelityDetail["arm"] {
  return value === "treatment" || value === "control" ? value : null;
}

function coveredOf(value: unknown): StoryCovered | null {
  return typeof value === "string" &&
    (STORY_COVERED_VALUES as readonly string[]).includes(value)
    ? (value as StoryCovered)
    : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function count(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : null;
}

// ---------------------------------------------------------------------------
// Copy shared by the badge and the coverage panel.
// ---------------------------------------------------------------------------

export type NarrationContextCopy = {
  title: string;
  subtitle: string;
  tone: "new" | "control" | "fallback" | "old" | "unknown";
};

/** Plain-English glosses for known `fallback` reasons (contract §8, CSF-H1). */
const FALLBACK_REASON_COPY = new Map<string, string>([
  [
    "legacy_after_authoring_failure",
    "authoring with the customer story failed, so the narration was re-authored once with the old context",
  ],
]);

/** The tooltip line for a fallback reason, e.g. "Fallback: <reason> (<gloss>)". */
function fallbackReasonLine(fallback: string): string {
  const gloss = FALLBACK_REASON_COPY.get(fallback);
  return `Fallback: ${fallback}${gloss ? ` (${gloss})` : ""}`;
}

export function narrationContextCopy(
  fidelity: Exclude<StoryFidelity, { kind: "loading" }>,
): NarrationContextCopy {
  switch (fidelity.kind) {
    case "treatment":
      return {
        title: "New narration context: customer story",
        subtitle: fidelity.isInternal
          ? "A/B treatment · internal"
          : "A/B treatment",
        tone: "new",
      };
    case "treatment_fallback":
      return {
        title: "Old narration context",
        subtitle: "A/B treatment · fell back after an authoring failure",
        tone: "fallback",
      };
    case "control":
      return {
        title: "Old narration context",
        subtitle:
          fidelity.arm === "treatment"
            ? "A/B treatment arm, story measured, not used"
            : "A/B control (story measured, not used)",
        tone: "control",
      };
    case "legacy":
      return {
        title: "Old narration context",
        subtitle: "before story fidelity",
        tone: "old",
      };
    case "unavailable":
      return {
        title: "Narration context unknown",
        subtitle:
          fidelity.reason === "script_error"
            ? "script could not be loaded"
            : "no script yet",
        tone: "unknown",
      };
  }
}

/** Spoken/must-say as "4/5", or null when coverage was not measured. */
export function coverageRatio(coverage: StoryCoverage | null): string | null {
  if (!coverage || coverage.spoken === null || coverage.mustSay === null) {
    return null;
  }
  return `${coverage.spoken}/${coverage.mustSay}`;
}

/** The tooltip lines: experiment, rule, ledger status, coverage. */
export function storyFidelityDetailLines(fidelity: StoryFidelity): string[] {
  switch (fidelity.kind) {
    case "loading":
      return ["Loading the script…"];
    case "unavailable":
      return [
        fidelity.reason === "script_error"
          ? "The script JSON could not be loaded, so the narration context is unknown."
          : "This Echo has no script yet, so the narration context is unknown.",
      ];
    case "legacy":
      return [
        "No story-fidelity summary in this Echo's script: it was made before the customer-story system, or outside B2C.",
      ];
    case "treatment":
    case "treatment_fallback":
    case "control": {
      const ratio = coverageRatio(fidelity.coverage);
      const ledgerCounts = [
        fidelity.itemCount !== null ? `${fidelity.itemCount} items` : null,
        fidelity.mustSayCount !== null
          ? `${fidelity.mustSayCount} must-say`
          : null,
      ].filter(Boolean);
      return [
        ...(fidelity.fallback ? [fallbackReasonLine(fidelity.fallback)] : []),
        `Experiment: ${fidelity.experimentId ?? "unknown"}`,
        `Rule: ${fidelity.ruleId ?? (fidelity.source === "summary" ? "base value (no rule)" : "unknown")}`,
        `Ledger: ${fidelity.ledgerStatus ?? "unknown"}${
          ledgerCounts.length ? ` · ${ledgerCounts.join(" · ")}` : ""
        }`,
        ratio
          ? `Coverage${fidelity.coverage?.stage ? ` (${fidelity.coverage.stage})` : ""}: ${ratio} must-say spoken${
              fidelity.coverage?.status ? ` · ${fidelity.coverage.status}` : ""
            }`
          : "Coverage: not measured",
        ...(fidelity.source === "narration_plan"
          ? ["Source: narration plan (no summary in the script diagnostics)"]
          : []),
      ];
    }
  }
}
